/**
 * Holiday avatar badges - a small seasonal glyph tucked into the corner of a
 * person's avatar bubble, live during whichever window lib/season.tsx says
 * is on (Halloween pumpkins/ghosts/webs, Thanksgiving turkeys, and so on).
 * Deterministic per name (a hash, not Math.random()) so the same person
 * wears the same badge everywhere and on every render within a season,
 * rather than flickering between icons on a re-render.
 *
 * Bobbie, 2026-08-17: "add the pumpkin and then maybe a ghost or a
 * spiderweb... on everybody's name" - "mix it up randomly per person."
 * Same evening: people can also PICK their own (seasonBadgePick.ts);
 * resolveSeasonBadge below applies a pick when the avatar being drawn is
 * this device's own person, and falls back to the dealt badge otherwise.
 */
import type { SeasonDef } from './season';
import { getSeasonBadgePick } from './seasonBadgePick';

export function seasonalAvatarBadge(label: string, badges: readonly string[]): string {
  if (badges.length === 0) return '';
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  return badges[hash % badges.length];
}

/**
 * The badge an avatar should wear: the pick stored on this device under
 * this exact label (picks are saved under every label the app renders a
 * person by - see seasonBadgePick.ts), the name-dealt badge otherwise.
 */
export function resolveSeasonBadge(label: string, season: SeasonDef): string {
  const pick = getSeasonBadgePick(season.key, [label]);
  if (pick?.badge && season.badges.includes(pick.badge)) return pick.badge;
  return seasonalAvatarBadge(label, season.badges);
}
